import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { AdminRoute } from "@/components/AdminRoute";
import OAuthConsent from "./pages/OAuthConsent";
import SoilLibrary from "./pages/SoilLibrary";
import CleanFillGuide from "./pages/CleanFillGuide";
import { DeferredMount } from "@/components/DeferredMount";
import Index from "./pages/Index";

const OnboardingGate = lazy(() => import("./components/OnboardingGate").then(m => ({ default: m.OnboardingGate })));
const RoleSelectionDialog = lazy(() => import("./components/auth/RoleSelectionDialog").then(m => ({ default: m.RoleSelectionDialog })));
const MaterialsChatbot = lazy(() => import("./components/chatbot/MaterialsChatbot").then(m => ({ default: m.MaterialsChatbot })));
const CommandPalette = lazy(() => import("./components/CommandPalette").then(m => ({ default: m.CommandPalette })));
const MobileBottomNav = lazy(() => import("./components/layout/MobileBottomNav").then(m => ({ default: m.MobileBottomNav })));

const Marketplace = lazy(() => import("./pages/Marketplace"));
const ListingDetail = lazy(() => import("./pages/ListingDetail"));
const PostListing = lazy(() => import("./pages/PostListing"));
const Analysis = lazy(() => import("./pages/Analysis"));
const Trucking = lazy(() => import("./pages/Trucking"));
const Login = lazy(() => import("./pages/Login"));
const Register = lazy(() => import("./pages/Register"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Messages = lazy(() => import("./pages/Messages"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const Contact = lazy(() => import("./pages/Contact"));
const Jobs = lazy(() => import("./pages/Jobs"));
const Manifests = lazy(() => import("./pages/Manifests"));
const Reports = lazy(() => import("./pages/Reports"));
const DataSummary = lazy(() => import("./pages/DataSummary"));
const LabCheck = lazy(() => import("./pages/LabCheck"));
const BuyerPortal = lazy(() => import("./pages/BuyerPortal"));
const EvidenceTimelinePage = lazy(() => import("./pages/EvidenceTimelinePage"));
const PackageWizard = lazy(() => import("./pages/PackageWizard"));
const Compare = lazy(() => import("./pages/Compare"));
const Facilities = lazy(() => import("./pages/Facilities"));
const Prequalify = lazy(() => import("./pages/Prequalify"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const WebVitalsAdmin = lazy(() => import("./pages/admin/WebVitals"));
const StaffReviewsAdmin = lazy(() => import("./pages/admin/StaffReviews"));
const PackagesAdmin = lazy(() => import("./pages/admin/Packages"));
const UsersAdmin = lazy(() => import("./pages/admin/Users"));
const StaffLogin = lazy(() => import("./pages/StaffLogin"));
const ReportDetailAdmin = lazy(() => import("./pages/admin/ReportDetail"));
const GoogleTrafficAdmin = lazy(() => import("./pages/admin/GoogleTraffic"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Suspense fallback={null}>
            <RoleSelectionDialog />
            <OnboardingGate />
            <DeferredMount>
              <CommandPalette />
            </DeferredMount>
          </Suspense>
          <Suspense fallback={null}>
            <Routes>
              <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
              <Route path="/" element={<Index />} />
              <Route path="/marketplace" element={<Marketplace />} />
              <Route path="/marketplace/:id" element={<ListingDetail />} />
              <Route path="/compare" element={<Compare />} />
              <Route path="/post-listing" element={<ProtectedRoute><PostListing /></ProtectedRoute>} />
              <Route path="/analysis" element={<Analysis />} />
              <Route path="/trucking" element={<Trucking />} />
              <Route path="/.lovable/oauth/consent" element={<OAuthConsent />} />
              <Route path="/login" element={<Login />} />
              <Route path="/staff/login" element={<StaffLogin />} />
              <Route path="/register" element={<Register />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
              <Route path="/messages" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
              <Route path="/jobs" element={<ProtectedRoute><Jobs /></ProtectedRoute>} />
              <Route path="/manifests" element={<ProtectedRoute><Manifests /></ProtectedRoute>} />
              <Route path="/reports" element={<ProtectedRoute><Reports /></ProtectedRoute>} />
              <Route path="/reports/data" element={<ProtectedRoute><DataSummary /></ProtectedRoute>} />
              <Route path="/lab-check" element={<ProtectedRoute><LabCheck /></ProtectedRoute>} />
              <Route path="/guides/clean-fill-vs-regular-dirt" element={<CleanFillGuide />} />
              <Route path="/library" element={<SoilLibrary />} />
              <Route path="/buyer" element={<ProtectedRoute><BuyerPortal /></ProtectedRoute>} />
              <Route path="/reports/timeline" element={<ProtectedRoute><EvidenceTimelinePage /></ProtectedRoute>} />
              <Route path="/package/new" element={<ProtectedRoute><PackageWizard /></ProtectedRoute>} />
              <Route path="/facilities" element={<ProtectedRoute><Facilities /></ProtectedRoute>} />
              <Route path="/prequalify" element={<ProtectedRoute><Prequalify /></ProtectedRoute>} />
              <Route path="/unsubscribe" element={<Unsubscribe />} />
              <Route path="/admin/web-vitals" element={<AdminRoute><WebVitalsAdmin /></AdminRoute>} />
              <Route path="/admin/packages" element={<AdminRoute><PackagesAdmin /></AdminRoute>} />
              <Route path="/admin/staff-reviews" element={<AdminRoute><StaffReviewsAdmin /></AdminRoute>} />
              <Route path="/admin/reports/:id" element={<AdminRoute><ReportDetailAdmin /></AdminRoute>} />
              <Route path="/admin/google-traffic" element={<AdminRoute><GoogleTrafficAdmin /></AdminRoute>} />
              <Route path="/admin/users" element={<AdminRoute><UsersAdmin /></AdminRoute>} />
              <Route path="/contact" element={<Contact />} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          <Suspense fallback={null}>
            <DeferredMount>
              <MaterialsChatbot />
            </DeferredMount>
            <DeferredMount onInteraction={false}>
              <MobileBottomNav />
            </DeferredMount>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
